import LightModeIcon from '@mui/icons-material/LightMode'
import Button from '@mui/material/Button'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import { BoardingIcon } from 'components-ui/src/asset/boarding_icon'
import { GroomingIcon } from 'components-ui/src/asset/grooming_icon'

import { styled, Theme } from '@mui/material/styles'
import { useBookingContext } from '@providers/booking'
import { useRoutes } from 'app/use_routes'
import { useCallback } from 'react'
import TagManager from 'react-gtm-module'
import { useParams } from 'react-router-dom'
import { Types } from 'service-api'

const StyledButton = styled(Button)(({ theme }: { theme: Theme }) => ({
  backgroundColor: theme.palette?.primary?.shade30,
  borderRadius: '30px',
  padding: '16px 24px',
  border: `1px solid ${theme.palette?.primary?.main}`,
  boxShadow: 'none',
  color: theme.palette?.primary?.main,
  '&:hover': {
    backgroundColor: theme.palette?.primary?.shade30,
  },
}))

const availableServiceTypes = ['boarding', 'daycare', 'grooming']

export const ServiceTypeSelector = ({
  isLoading,
  services,
  onSelect,
}: {
  isLoading: boolean
  services: Types.LocationServiceTypeClientDto[]
  onSelect?: () => void
}) => {
  const bookingCtx = useBookingContext()
  const { serviceTypeName } = useParams()
  const { navigateRoute } = useRoutes()
  const onClickButton = useCallback(
    (ev: React.MouseEvent<HTMLButtonElement>) => {
      const { currentTarget: t } = ev
      if (serviceTypeName !== t.id) {
        bookingCtx.dispatch({ type: 'RESET_STATE' })
        onSelect?.()
        navigateRoute('searchServiceType', { serviceTypeName: t.id })
        TagManager.dataLayer({
          dataLayer: {
            event: 'SELECT_SERVICE_TYPE',
            serviceType: {
              name: t.id,
            },
          },
        })
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [bookingCtx, navigateRoute, serviceTypeName]
  )

  const renderPlaceholders = () => {
    return (
      <>
        <Skeleton width={130} height={54} sx={{ transform: 'scale(1,1)', borderRadius: '30px' }} />
        <Skeleton width={130} height={54} sx={{ transform: 'scale(1,1)', borderRadius: '30px' }} />
      </>
    )
  }

  const buttonIcon = (serviceTypeName: string) => {
    switch (serviceTypeName) {
      case 'boarding':
        return <BoardingIcon />
      case 'daycare':
        return <LightModeIcon />
      case 'grooming':
        return <GroomingIcon viewBox="0 -2 22 22" />
      default:
        return <LightModeIcon />
    }
  }

  const renderButtons = () => {
    return services.map((st) => {
      if (availableServiceTypes.includes(st.serviceType!.name!) && st.canBeStandalone) {
        const buttonProps: GenericSimpleBag = {
          id: st.serviceType?.name,
          startIcon: buttonIcon(st.serviceType?.name || ''),
          variant: serviceTypeName === st.serviceType?.name ? 'contained' : 'text',
          size: 'large',
          onClick: onClickButton,
        }

        if (serviceTypeName === st.serviceType?.name) {
          return (
            <StyledButton key={st.serviceType?.id} {...buttonProps}>
              {st.serviceType?.displayName}
            </StyledButton>
          )
        } else {
          return (
            <Button key={st.serviceType?.id} {...buttonProps} sx={{ px: 3, py: 2 }}>
              {st.serviceType?.displayName}
            </Button>
          )
        }
      }
      return null
    })
  }

  if (!isLoading && services.length < 2) {
    return null
  }

  return (
    <Stack direction="row" spacing={2} alignItems="center" justifyContent="center">
      {isLoading ? renderPlaceholders() : renderButtons()}
    </Stack>
  )
}
